import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-location';
import Input from '@amzn/awsui-components-react/polaris/input';

import pageCatalog from 'pages/pageCatalog';
import { getActualUserName, getMagicMovieAvatar, setMagicMovieAvatar, setMagicMovieUserName } from 'store/magicMovie';
import { useAppDispatch, useAppSelector } from 'store/utils/hooks';
import Avatar from '../components/avatar';
import { avatarColors } from '../constants';

import './styles.css';
import { NonCancelableCustomEvent } from '@amzn/awsui-components-react/polaris/internal/events';
import { BaseChangeDetail } from '@amzn/awsui-components-react/polaris/input/interfaces';

export const MagicMovieChooseUserName: React.FC = () => {
  const dispatch = useAppDispatch();
  const userName = useAppSelector(getActualUserName);
  const selectedAvatar = useAppSelector(getMagicMovieAvatar);
  const { t } = useTranslation(['magicMovie']);
  const navigate = useNavigate();
  const [pageSelectedAvatar, setPageSelectedAvatar] = useState(selectedAvatar);
  const [pageUserName, setPageUserName] = useState(userName);
  const [invalidUserName, setInvalidUserName] = useState(false);

  const updateUserValues = () => {
    dispatch(
      setMagicMovieAvatar({
        avatarNumber: pageSelectedAvatar,
      })
    );
    dispatch(
      setMagicMovieUserName({
        userName: pageUserName,
      })
    );
  };

  /**
   * Handles changes when user enters the username
   */
  const handleOnChange = (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
    setPageUserName(event.detail.value);
  };

  /**
   * Handles when user leaves the input box
   */
  const handleOnBlur = () => {
    // Validate username input
    if (pageUserName?.length > 64 || (pageUserName?.length > 0 && !pageUserName?.match(/^[a-zA-Z0-9\- ]+$/))) {
      setInvalidUserName(true);
    } else {
      setPageUserName(pageUserName.trim());
      setInvalidUserName(false);
    }
  };

  return (
    <div id="game" className="gameContainer" data-test-id="magicMovieUserNameRoot">
      <div>
        <h1 className="header-spaced white">{t('chooseUserName.newUser')}</h1>
        <div className="title-spaced">
          <h5 className="blue label-title">{t('chooseUserName.title')}</h5>
          <div>
            <Input
              id="username"
              className="customInput"
              ariaRequired
              autoFocus
              value={pageUserName}
              invalid={invalidUserName}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
              placeholder="Username"
              data-test-id="magicMovieUserNameInput"
            />
          </div>
        </div>
        <div className="title-spaced">
          <h5 className="blue label-title">{t('chooseUserName.portrait')}</h5>
          <div className="avatar-flex-container">
            {avatarColors.map(({ peel, color }, key: number) => (
              <div
                key={key}
                onClick={() => {
                  setPageSelectedAvatar(key);
                }}
                data-avatar={key}
                style={{ backgroundColor: color }}
                className={`rounded-avatar portrait ${pageSelectedAvatar === key ? 'selected' : ''}`}
              >
                <Avatar id={key} peel={peel} />
              </div>
            ))}
          </div>
        </div>
        <button
          className="customButton"
          style={{
            maxWidth: '400px',
            fontSize: '24px',
            padding: '20px',
            borderRadius: '4px',
            background: `${invalidUserName ? 'grey' : 'orange'}`,
            color: '#16191F',
          }}
          disabled={invalidUserName}
          onClick={() => {
            updateUserValues();
            navigate({ to: pageCatalog['MagicMovieColdStart'].route });
          }}
          data-test-id="magicMovieUserNameContinueButton"
        >
          {t('chooseUserName.save')}
        </button>
      </div>
    </div>
  );
};
